import { useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { captureCustomEvent } from "../Sentry/utils";

const AuthError = () => {
  const [searchParams] = useSearchParams();
  const reason = searchParams.get("reason");
  const email = searchParams.get("email");
  const logoutUrl = `${import.meta.env.VITE_BACKEND_URL}/sso/cognito/logout`;

  const restartLogin = () => {
    window.location.href = logoutUrl;
  };

  useEffect(() => {
    if (!reason) return;

    captureCustomEvent({
      message: "SSO login error",
      level: "error",
      debugData: {
        reason,
        cognitoEmail: email,
        currentPath: window.location.pathname,
        search: window.location.search,
      },
    });
  }, [reason, email]);

  const details = useMemo(() => {
    if (reason === "missing_user") {
      return "Your SSO account is authenticated, but no matching user was found in the application database.";
    }
    if (reason === "missing_email") {
      return "The SSO provider did not return a usable email address to identify your account.";
    }
    if (reason === "session_unavailable") {
      return "The user session could not be loaded after SSO authentication.";
    }
    if (reason === "no_access") {
      return "Your account is authenticated, but no application workspace is currently authorized for your profile.";
    }
    return "SSO login failed. This can happen when the email returned by SSO does not exactly match the email stored in the database.";
  }, [reason]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 p-6">
      <div className="w-full max-w-xl rounded-lg border border-red-200 bg-white p-6 shadow-sm">
        <h1 className="text-xl font-semibold text-red-700">SSO Login Error</h1>
        <p className="mt-3 text-sm text-slate-700">{details}</p>
        <p className="mt-2 text-sm text-slate-700">
          Contact Quantilia at{" "}
          <span className="font-semibold text-slate-900">
            datasupport@quantilia.com
          </span>{" "}
          and include your SSO email and your application user email.
        </p>
        {email && (
          <p className="mt-2 break-all rounded bg-slate-100 px-3 py-2 text-xs text-slate-700">
            Detected SSO email: {email}
          </p>
        )}
        <div className="mt-5 flex flex-wrap gap-3">
          <button
            className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
            onClick={restartLogin}
          >
            Retry login
          </button>
        </div>
      </div>
    </div>
  );
};

export default AuthError;
